/**
 * Asset optimization utilities for improved loading performance
 * Handles image optimization, preloading, and resource hints
 */

// Resource hint types
type ResourceHint = 'preload' | 'prefetch' | 'preconnect' | 'dns-prefetch';

// Asset types
type AssetType = 'image' | 'script' | 'style' | 'font' | 'document';

// Priority levels
type Priority = 'high' | 'low' | 'auto';

interface ResourceHintOptions {
  href: string;
  as?: AssetType;
  type?: string;
  crossorigin?: 'anonymous' | 'use-credentials';
  media?: string;
  sizes?: string;
  imagesrcset?: string;
  imagesizes?: string;
  fetchpriority?: Priority;
}

// Add resource hint to document head
export const addResourceHint = (hint: ResourceHint, options: ResourceHintOptions): HTMLLinkElement => {
  const link = document.createElement('link');
  link.rel = hint;
  link.href = options.href;

  if (options.as) link.setAttribute('as', options.as);
  if (options.type) link.setAttribute('type', options.type);
  if (options.crossorigin) link.setAttribute('crossorigin', options.crossorigin);
  if (options.media) link.setAttribute('media', options.media);
  if (options.sizes) link.setAttribute('sizes', options.sizes);
  if (options.imagesrcset) link.setAttribute('imagesrcset', options.imagesrcset);
  if (options.imagesizes) link.setAttribute('imagesizes', options.imagesizes);
  if (options.fetchpriority) link.setAttribute('fetchpriority', options.fetchpriority);

  document.head.appendChild(link);
  return link;
};

// Remove resource hint
export const removeResourceHint = (link: HTMLLinkElement): void => {
  if (link.parentNode) {
    link.parentNode.removeChild(link);
  }
};

// Preload critical assets
export const preloadCriticalAssets = (): HTMLLinkElement[] => {
  const criticalAssets = [
    // Critical CSS
    { href: '/static/css/main.css', as: 'style' as AssetType, fetchpriority: 'high' as Priority },
    // Critical fonts
    { href: '/fonts/inter-var.woff2', as: 'font' as AssetType, type: 'font/woff2', crossorigin: 'anonymous' as const },
    // Critical images
    { href: '/logo.svg', as: 'image' as AssetType, fetchpriority: 'high' as Priority },
  ];

  return criticalAssets.map(asset => 
    addResourceHint('preload', asset)
  );
};

// Preconnect to external domains
export const preconnectExternalDomains = (): HTMLLinkElement[] => {
  const domains = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
    'https://api.forecast.s.pankowalski.pl',
    // Add other external domains as needed
  ];

  return domains.map(href => 
    addResourceHint('preconnect', { href, crossorigin: 'anonymous' })
  );
};

// Image optimization utilities
export const generateResponsiveImageSizes = (
  baseSrc: string,
  breakpoints: number[] = [320, 640, 768, 1024, 1280, 1920]
): string => {
  const extension = baseSrc.split('.').pop();
  const baseName = baseSrc.replace(/\.[^/.]+$/, '');
  
  return breakpoints
    .map(width => `${baseName}_${width}w.${extension} ${width}w`)
    .join(', ');
};

// Generate WebP and AVIF sources
export const generateModernImageSources = (
  baseSrc: string,
  breakpoints: number[] = [320, 640, 768, 1024, 1280, 1920]
): Array<{ srcSet: string; type: string }> => {
  const baseName = baseSrc.replace(/\.[^/.]+$/, '');
  
  return [
    {
      srcSet: breakpoints.map(width => `${baseName}_${width}w.avif ${width}w`).join(', '),
      type: 'image/avif'
    },
    {
      srcSet: breakpoints.map(width => `${baseName}_${width}w.webp ${width}w`).join(', '),
      type: 'image/webp'
    }
  ];
};

// Lazy loading with Intersection Observer
export const createLazyLoader = (
  options: IntersectionObserverInit = {}
): IntersectionObserver => {
  const defaultOptions: IntersectionObserverInit = {
    rootMargin: '50px',
    threshold: 0.1,
    ...options
  };

  return new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const target = entry.target as HTMLElement;
        
        // Handle images
        if (target.tagName === 'IMG') {
          const img = target as HTMLImageElement;
          const dataSrc = img.dataset.src;
          const dataSrcset = img.dataset.srcset;
          
          if (dataSrc) {
            img.src = dataSrc;
            img.removeAttribute('data-src');
          }
          
          if (dataSrcset) {
            img.srcset = dataSrcset;
            img.removeAttribute('data-srcset');
          }
          
          img.classList.remove('lazy');
          img.classList.add('loaded');
        }
        
        // Handle background images
        if (target.dataset.bgSrc) {
          target.style.backgroundImage = `url(${target.dataset.bgSrc})`;
          target.removeAttribute('data-bg-src');
        }
        
        // Handle iframes
        if (target.tagName === 'IFRAME') {
          const iframe = target as HTMLIFrameElement;
          const dataSrc = iframe.dataset.src;
          
          if (dataSrc) {
            iframe.src = dataSrc;
            iframe.removeAttribute('data-src');
          }
        }
        
        // Stop observing this element
        entry.target.classList.add('observed');
      }
    });
  }, defaultOptions);
};

// Progressive image loading with blur effect
export const createProgressiveImageLoader = (): {
  loadImage: (src: string, placeholder?: string) => Promise<HTMLImageElement>;
  createBlurPlaceholder: (width: number, height: number) => string;
} => {
  const loadImage = (src: string, placeholder?: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => resolve(img);
      img.onerror = reject;
      
      // Set loading priority if supported
      if ('loading' in img) {
        img.loading = 'lazy';
      }
      
      // Set decode hint if supported
      if ('decoding' in img) {
        img.decoding = 'async';
      }
      
      img.src = src;
    });
  };

  const createBlurPlaceholder = (width: number, height: number): string => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = width;
    canvas.height = height;
    
    if (ctx) {
      // Create gradient
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#f3f4f6');
      gradient.addColorStop(1, '#e5e7eb');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }
    
    return canvas.toDataURL('image/jpeg', 0.1);
  };

  return { loadImage, createBlurPlaceholder };
};

// Asset preloading manager
export class AssetPreloader {
  private preloadedAssets = new Set<string>();
  private preloadPromises = new Map<string, Promise<void>>();

  async preloadImage(src: string, priority: Priority = 'low'): Promise<void> {
    if (this.preloadedAssets.has(src)) {
      return this.preloadPromises.get(src) || Promise.resolve();
    }

    const promise = new Promise<void>((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.preloadedAssets.add(src);
        resolve();
      };
      
      img.onerror = () => {
        reject(new Error(`Failed to preload image: ${src}`));
      };

      // Set priority if supported
      if ('fetchPriority' in img) {
        (img as any).fetchPriority = priority;
      }

      img.src = src;
    });

    this.preloadPromises.set(src, promise);
    return promise;
  }

  async preloadScript(src: string, priority: Priority = 'low'): Promise<void> {
    if (this.preloadedAssets.has(src)) {
      return this.preloadPromises.get(src) || Promise.resolve();
    }

    const promise = new Promise<void>((resolve, reject) => {
      const link = addResourceHint('preload', {
        href: src,
        as: 'script',
        fetchpriority: priority
      });

      link.onload = () => {
        this.preloadedAssets.add(src);
        resolve();
      };

      link.onerror = () => {
        reject(new Error(`Failed to preload script: ${src}`));
      };
    });

    this.preloadPromises.set(src, promise);
    return promise;
  }

  async preloadFont(src: string, type = 'font/woff2'): Promise<void> {
    if (this.preloadedAssets.has(src)) {
      return this.preloadPromises.get(src) || Promise.resolve();
    }

    const promise = new Promise<void>((resolve, reject) => {
      const link = addResourceHint('preload', {
        href: src,
        as: 'font',
        type,
        crossorigin: 'anonymous',
        fetchpriority: 'high'
      });

      link.onload = () => {
        this.preloadedAssets.add(src);
        resolve();
      };

      link.onerror = () => {
        reject(new Error(`Failed to preload font: ${src}`));
      };
    });

    this.preloadPromises.set(src, promise);
    return promise;
  }

  isPreloaded(src: string): boolean {
    return this.preloadedAssets.has(src);
  }

  getPreloadedCount(): number {
    return this.preloadedAssets.size;
  }
}

// Global asset preloader instance
export const assetPreloader = new AssetPreloader();

// Initialize asset optimization
export const initializeAssetOptimization = (): void => {
  // Preconnect to external domains
  preconnectExternalDomains();
  
  // Preload critical assets
  preloadCriticalAssets();
  
  // Set up lazy loading for images
  const lazyLoader = createLazyLoader();
  
  // Observe all lazy images
  document.querySelectorAll('img[data-src], iframe[data-src], [data-bg-src]').forEach(el => {
    lazyLoader.observe(el);
  });
  
  // Set up mutation observer for dynamically added lazy elements
  const mutationObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          
          // Check if the element itself is lazy
          if (element.hasAttribute('data-src') || element.hasAttribute('data-bg-src')) {
            lazyLoader.observe(element);
          }
          
          // Check for lazy elements within the added node
          element.querySelectorAll?.('img[data-src], iframe[data-src], [data-bg-src]').forEach(el => {
            lazyLoader.observe(el);
          });
        }
      });
    });
  });
  
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
};
